/**
 * DiffNav — the unified WorkspaceNav in pure diff mode.
 *
 * A convenience wrapper for surfaces that only have a changeset (the diff
 * viewers): it builds the minimal diff-only NavSource (no files, no live
 * model, no mutations) so the rail renders the ChangesView body with the same
 * header + search chrome every other surface gets. Replaces the legacy
 * ChangesHierarchy mount.
 */
import { useMemo, type ReactElement } from "react";
import type { ChangeSet, EntityRef } from "@archlang/engine";
import { WorkspaceNav } from "../primitives/WorkspaceNav.js";
import type { NavSource } from "./NavSource.js";

export interface DiffNavProps {
  readonly changeSet: ChangeSet;
  readonly selected: EntityRef | null;
  readonly onFocus: (ref: EntityRef) => void;
  readonly className?: string;
}

const noop = (): void => {};
const EMPTY_SELECTION = {
  moduleId: null,
  interfaceKey: null,
  processId: null,
  typeName: null,
  activeFile: null,
} as const;

export function DiffNav({ changeSet, selected, onFocus, className }: DiffNavProps): ReactElement {
  const source = useMemo<NavSource>(
    () => ({
      files: () => [],
      model: () => null,
      selection: () => EMPTY_SELECTION,
      open: noop,
      reveal: noop,
      selectModule: noop,
      diff: () => changeSet,
      diffSelection: () => selected,
      focus: onFocus,
    }),
    [changeSet, selected, onFocus],
  );
  return <WorkspaceNav source={source} className={className} />;
}
